import { useTranslate } from '@refinedev/core';
import { Form, Input, Radio, Typography } from 'antd';
import { useEffect, useState } from 'react';
import {
  SectionDrawer,
  ToolDatePicker,
  dateOfDay,
  dayOf,
} from '../../components/ToolSection';
import {
  addDays,
  emptyNightDraft,
  nightInput,
  nightProblem,
  nightSelectable,
  type NightDraft,
} from './model';
import { useSleepCommand } from './useSleepCommand';

/**
 * E45 · "Enter a night" — the paper diary typed in, as a side drawer on the Nights card (design PORTAL-SECTIONS-2026-10-22 §2.1 (d)1).
 * The night starts on YESTERDAY and the picker refuses what the server refuses (today or later; more than fourteen days back); a
 * problem names the first thing to fix and stays until the next edit.
 */
type Tx = (key: string, options?: Record<string, unknown>) => string;
const NIGHT_KINDS = ['slept', 'noSleep', 'skipped'] as const;
const KIND_KEY = {
  slept: 'Slept',
  noSleep: 'NoSleep',
  skipped: 'Skipped',
} as const;

export const SleepNightDrawer = ({
  open,
  clientId,
  onClose,
  onSaved,
}: {
  open: boolean;
  clientId: string;
  onClose: () => void;
  /** The night that was saved — the caller closes, toasts and shows its week. */
  onSaved: (night: string) => void;
}) => {
  const t = useTranslate() as unknown as Tx;
  const { run, isLoading } = useSleepCommand();
  const today = dayOf(new Date());
  const opening = () => emptyNightDraft(addDays(today, -1));
  const [night, setNight] = useState<NightDraft>(opening);
  const [error, setError] = useState<string | null>(null);
  const [initial, setInitial] = useState<NightDraft>(opening);

  useEffect(() => {
    if (!open) return;
    const fresh = opening();
    setNight(fresh);
    setInitial(fresh);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = (p: Partial<NightDraft>) => {
    setError(null);
    setNight((d) => ({ ...d, ...p }));
  };
  const text = (
    key: keyof NightDraft,
    label: string,
    placeholder: string,
    testId: string,
  ) => (
    <Form.Item label={label}>
      <Input
        placeholder={placeholder}
        value={night[key] as string}
        onChange={(e) => patch({ [key]: e.target.value })}
        data-testid={testId}
      />
    </Form.Item>
  );

  const onSave = async () => {
    const problem = nightProblem(night);
    if (problem) {
      setError(t(`sleepDiary.enter.${problem}`));
      return;
    }
    const input = nightInput(night, clientId, today);
    if (!input) {
      setError(t('sleepDiary.enter.timesRequired'));
      return;
    }
    const res = await run(
      'confirmSleepNight',
      input as unknown as Record<string, unknown>,
      t('sleepDiary.enter.error'),
    );
    if (res.ok) onSaved(night.night);
    else setError(res.message);
  };

  return (
    <SectionDrawer
      open={open}
      title={t('sleepDiary.enter.title')}
      testId="sleep-hub-enter"
      context={
        <Typography.Text type="secondary">
          {t('sleepDiary.enter.body')}
        </Typography.Text>
      }
      saveLabel={t('sleepDiary.enter.save')}
      saving={isLoading}
      error={error}
      dirty={JSON.stringify(night) !== JSON.stringify(initial)}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Form layout="vertical" component="div">
        <Form.Item label={t('sleepDiary.enter.night')}>
          <ToolDatePicker
            value={night.night ? dateOfDay(night.night) : null}
            onChange={(d) => patch({ night: d ? dayOf(d) : '' })}
            disabledDate={(d) => !nightSelectable(dayOf(d), today)}
            testId="sleep-hub-enter-night"
          />
        </Form.Item>
        <Form.Item label={t('sleepDiary.enter.kind')}>
          <Radio.Group
            value={night.kind}
            onChange={(e) => patch({ kind: e.target.value })}
            data-testid="sleep-hub-enter-kind"
          >
            {NIGHT_KINDS.map((k) => (
              <Radio.Button
                key={k}
                value={k}
                data-testid={`sleep-hub-enter-kind-${k}`}
              >
                {t(`sleepDiary.enter.kind${KIND_KEY[k]}`)}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
        {night.kind !== 'skipped' ? (
          <>
            {text(
              'bedTime',
              t('sleepDiary.enter.bedTime'),
              '23:10',
              'sleep-hub-enter-bedTime',
            )}
            {night.kind === 'slept' ? (
              <>
                {text(
                  'lightsOut',
                  t('sleepDiary.enter.lightsOut'),
                  '23:25',
                  'sleep-hub-enter-lightsOut',
                )}
                {text(
                  'sleepOnsetMinutes',
                  t('sleepDiary.enter.sleepOnset'),
                  '35',
                  'sleep-hub-enter-sleepOnset',
                )}
                {text(
                  'awakenings',
                  t('sleepDiary.enter.awakenings'),
                  '3',
                  'sleep-hub-enter-awakenings',
                )}
                {text(
                  'wakeMinutes',
                  t('sleepDiary.enter.wakeMinutes'),
                  '50',
                  'sleep-hub-enter-wakeMinutes',
                )}
                {text(
                  'finalWake',
                  t('sleepDiary.enter.finalWake'),
                  '06:20',
                  'sleep-hub-enter-finalWake',
                )}
              </>
            ) : null}
            {text(
              'outOfBed',
              t('sleepDiary.enter.outOfBed'),
              '07:05',
              'sleep-hub-enter-outOfBed',
            )}
            <Form.Item label={t('sleepDiary.enter.quality')}>
              <Radio.Group
                value={night.quality}
                onChange={(e) => patch({ quality: e.target.value })}
                data-testid="sleep-hub-enter-quality"
              >
                {[1, 2, 3, 4, 5].map((q) => (
                  <Radio.Button
                    key={q}
                    value={q}
                    data-testid={`sleep-hub-enter-quality-${q}`}
                  >
                    {q}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
          </>
        ) : null}
        <Form.Item label={t('sleepDiary.enter.note')}>
          <Input.TextArea
            value={night.note}
            onChange={(e) => patch({ note: e.target.value })}
            rows={2}
            maxLength={2000}
            data-testid="sleep-hub-enter-note"
          />
        </Form.Item>
        <Typography.Text type="secondary">{t('crisis.entryNote')}</Typography.Text>
      </Form>
    </SectionDrawer>
  );
};
